import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ProgressBar,
  Table,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import urls from "../../../../Utils/urls";
import NewDrawRequestModal from "./NewDrawRequestModal";
import {
  BsBuilding,
  BsFileEarmarkText,
  BsCurrencyDollar,
  BsGraphUpArrow,
  BsPerson,
  BsPlus,
  BsUpload,
  BsChevronRight,
  BsChevronDown,
  BsClipboardData,
  BsFileText,
  BsFlag,
  BsChatSquareDots,
  BsArrowLeft,
} from "react-icons/bs";
import "./LenderDrawsDashbaord.css";
import { useSelector } from "react-redux";
import axios from "axios";
import { getHeaders } from "../../../Documentation/utils";
import CommonGrid from "../../../../components/CommonGrid";

const activityUpdates = [];

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const currencyWithCents = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const coerceNumber = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? 0 : value;
  }
  if (typeof value === "string") {
    const normalized = value.replace(/[$,%\s]/g, "").replace(/,/g, "");
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const hasValue = (value) =>
  value !== null && value !== undefined && `${value}`.trim() !== "";

const formatDateValue = (value) => {
  if (!hasValue(value)) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
};

const formatDateTimeValue = (value) => {
  if (!hasValue(value)) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
};

const formatCurrencyValue = (value) =>
  hasValue(value) ? currencyWithCents.format(coerceNumber(value)) : "--";

const DRAW_FALLBACKS = {
  property: "Property not linked",
  account: "Account not linked",
  drawDate: "Date not available",
};

const getDrawDateSource = (item) =>
  item?.request_date ||
  item?.requestDate ||
  item?.creation_date ||
  item?.draw_date ||
  item?.drawDate ||
  "";

const getDrawAmountSource = (item) =>
  item?.draw_amount ||
  item?.requested_amount ||
  item?.requestedAmt ||
  item?.amount ||
  item?.drawAmount;

const normalizeLineItemValue = (item) => {
  const raw =
    item?.item_desc ||
    item?.itemDesc ||
    item?.line_item ||
    item?.lineItem ||
    "";
  if (hasValue(raw)) return { value: `${raw}`.trim(), missing: false };
  return { value: "No line item provided", missing: true };
};

const normalizeDrawHistoryItem = (item, index) => ({
  ...(() => {
    const normalizedLineItem = normalizeLineItemValue(item);
    return {
      lineItem: normalizedLineItem.value,
      lineItemMissing: normalizedLineItem.missing,
    };
  })(),
  id: `${item?.hdr_id || "hdr"}-${item?.line_id || index}`,
  property:
    item?.property ||
    item?.Property ||
    item?.property_address ||
    item?.Property_Address ||
    DRAW_FALLBACKS.property,
  account:
    item?.loan_number || item?.loanNumber || item?.LoanNumber || item?.account || DRAW_FALLBACKS.account,
  drawDate: hasValue(getDrawDateSource(item))
    ? formatDateValue(getDrawDateSource(item))
    : DRAW_FALLBACKS.drawDate,
  creationDate: hasValue(item?.creation_date)
    ? formatDateTimeValue(item?.creation_date)
    : "--",
  amount: formatCurrencyValue(getDrawAmountSource(item)),
});


const groupAndNormalizeDrawHistory = (items) => {
  const groups = {};
  items.forEach((item, index) => {
    const requestDate = getDrawDateSource(item);
    const loanrecid = item?.loanrecid || item?.loanRecId || "";
    const key =
      requestDate && loanrecid
        ? `${requestDate}_${loanrecid}`
        : `ungrouped-${index}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  });

  const result = [];

  Object.values(groups).forEach((groupItems) => {
    const first = groupItems[0];

    // Check if all items share the same status
    const allStatuses = groupItems.map(
      (item) => (item?.status === "MANUAL" ? "SUBMITTED" : item?.status) || "",
    );
    const uniqueStatuses = [...new Set(allStatuses.filter(Boolean))];
    const hasMixedStatuses = uniqueStatuses.length > 1;

    if (hasMixedStatuses) {
      // Mixed statuses → flatten each line item as its own row
      groupItems.forEach((item, i) => {
        const status = (item?.status === "MANUAL" ? "SUBMITTED" : item?.status) || "";
        const normalizedLineItem = normalizeLineItemValue(item);
        result.push({
          id: `${first?.hdr_id || "hdr"}-${item?.line_id || i}-flat`,
          property:
            item?.property ||
            item?.Property ||
            item?.property_address ||
            item?.Property_Address ||
            DRAW_FALLBACKS.property,
          account:
            item?.loan_number || item?.loanNumber || item?.LoanNumber || item?.account ||
            first?.loan_number || first?.loanNumber || first?.LoanNumber || first?.account || DRAW_FALLBACKS.account,
          loanrecid: item?.loanrecid || item?.loanRecId || first?.loanrecid || first?.loanRecId || "",
          drawDate: hasValue(getDrawDateSource(item))
            ? formatDateValue(getDrawDateSource(item))
            : DRAW_FALLBACKS.drawDate,
          creationDate: hasValue(item?.creation_date)
            ? formatDateTimeValue(item?.creation_date)
            : "--",
          lineItem: normalizedLineItem.value,
          lineItemMissing: normalizedLineItem.missing,
          amount: formatCurrencyValue(getDrawAmountSource(item)),
          status,
          rejectReason: item?.reject_reason || item?.rejectReason || item?.rejection_reason || "",
          subRows: undefined,
        });
      });
    } else {
      // Same status → group as before
      const totalAmount = groupItems.reduce(
        (sum, item) => sum + coerceNumber(getDrawAmountSource(item) || 0),
        0,
      );
      const isSingle = groupItems.length === 1;
      const groupStatus = uniqueStatuses[0] || "";

      const subRows = isSingle
        ? undefined
        : groupItems.map((item, i) => {
            const normalizedLineItem = normalizeLineItemValue(item);
            return {
              id: `${first?.hdr_id || "hdr"}-${item?.line_id || i}-sub`,
              property: "",
              account: "",
              drawDate: "",
              creationDate: "",
              lineItem: normalizedLineItem.value,
              lineItemMissing: normalizedLineItem.missing,
              amount: formatCurrencyValue(getDrawAmountSource(item)),
              status: groupStatus,
            };
          });

      const groupKey = `${first?.request_date || first?.creation_date || ""}__${first?.loanrecid || ""}`;
      const singleLineItem = normalizeLineItemValue(first);
      result.push({
        id: groupKey || `group-${first?.hdr_id}`,
        property:
          first?.property ||
          first?.Property ||
          first?.property_address ||
          first?.Property_Address ||
          DRAW_FALLBACKS.property,
        account:
          first?.loan_number || first?.loanNumber || first?.LoanNumber || first?.account || DRAW_FALLBACKS.account,
        loanrecid: first?.loanrecid || first?.loanRecId || "",
        drawDate: hasValue(getDrawDateSource(first))
          ? formatDateValue(getDrawDateSource(first))
          : DRAW_FALLBACKS.drawDate,
        creationDate: hasValue(first?.creation_date)
          ? formatDateTimeValue(first?.creation_date)
          : "--",
        lineItem: isSingle
          ? singleLineItem.value
          : `${groupItems.length} line items`,
        lineItemMissing: isSingle ? singleLineItem.missing : false,
        amount: currencyWithCents.format(totalAmount),
        status: groupStatus,
        rejectReason: first?.reject_reason || first?.rejectReason || first?.rejection_reason || "",
        subRows,
      });
    }
  });

  return result;
};

const normalizeProject = (item, index) => {
  const budget = coerceNumber(
    item?.budget ??
      item?.Budget ??
      item?.Current_Budget ??
      item?.current_budget ??
      item?.original_budget,
  );
  const funded = coerceNumber(
    item?.funded ??
      item?.Funded ??
      item?.Total_Funded ??
      item?.total_funded ??
      item?.prior_funded,
  );
  const holdback = coerceNumber(
    item?.holdback ??
      item?.Holdback ??
      item?.Holdback_Remaining ??
      item?.holdback_remaining ??
      item?.remaining_holdback,
  );

  return {
    id:
      item?.id ||
      item?.project_id ||
      item?.loan_id ||
      item?.loanId ||
      item?.LoanID ||
      item?.LoanRecid ||
      item?.recid ||
      `project-${index}`,
    address:
      item?.address ||
      item?.Property_Address ||
      item?.property_address ||
      item?.property ||
      item?.Property ||
      "",
    borrower:
      item?.borrower ||
      item?.Borrower ||
      item?.borrower_name ||
      item?.Borrower_Name ||
      item?.borrowerName ||
      "",
    type:
      item?.type ||
      item?.Project_Type ||
      item?.project_type ||
      item?.Loan_Type ||
      item?.loan_type ||
      "",
    budget,
    funded,
    holdback,
    ltc: coerceNumber(item?.ltc ?? item?.LTC ?? item?.loan_to_cost ?? 0),
    ltv: coerceNumber(item?.ltv ?? item?.LTV ?? item?.loan_to_value ?? 0),
    loanNumber:
      item?.loanNumber ||
      item?.LoanNumber ||
      item?.loan_number ||
      item?.Account ||
      item?.account ||
      "",
  };
};

const normalizeSummary = (summary, projects) => {
  const fallbackTotals = projects.reduce(
    (acc, project) => {
      acc.totalBudget += project.budget;
      acc.totalFunded += project.funded;
      acc.totalHoldback += project.holdback;
      return acc;
    },
    { totalBudget: 0, totalFunded: 0, totalHoldback: 0 },
  );

  const activeProjects =
    coerceNumber(summary?.activeProjects ?? summary?.active_projects ?? summary?.["Active Projects"]) ||
    projects.length;
  const totalBudget =
    coerceNumber(summary?.totalBudget ?? summary?.total_budget ?? summary?.["Total Budget"]) ||
    fallbackTotals.totalBudget;
  const totalFunded =
    coerceNumber(summary?.totalFunded ?? summary?.total_funded ?? summary?.["Total Funded"]) ||
    fallbackTotals.totalFunded;
  const remainingHoldback =
    coerceNumber(summary?.remainingHoldback ?? summary?.remaining_holdback ?? summary?.["Remaining Holdback"]) ||
    fallbackTotals.totalHoldback;

  return {
    activeProjects,
    totalBudget,
    totalFunded,
    remainingHoldback,
  };
};

const SummaryCard = ({ label, value, icon, className }) => (
  <Card className="summary-card">
    <Card.Body className="d-flex align-items-center justify-content-between">
      <div>
        <div className="summary-label">{label}</div>
        <div className="summary-value">{value}</div>
      </div>
      <div className={className}>{icon}</div>
    </Card.Body>
  </Card>
);

const ProjectCard = ({ project, onView }) => {
  const progress =
    project.budget > 0 ? (project.funded / project.budget) * 100 : null;
  const progressLabel = progress === null ? "--" : `${progress.toFixed(1)}%`;
  const progressValue = progress === null ? 0 : progress;
  const hasType = Boolean(project.type);

  return (
    <Card className="project-card">
      <Card.Body>
        <div className="d-flex align-items-start justify-content-between gap-3">
          <div>
            <Card.Title className="project-title">{project.address}</Card.Title>
            <div className="project-borrower">
              <BsPerson className="me-2" />
              {project.borrower}
            </div>
          </div>
          {hasType && (
            <span
              className={`project-badge ${project.type === "Ground Up" ? "ground" : "rehab"}`}
            >
              {project.type}
            </span>
          )}
        </div>

        <Row className="mt-3">
          <Col xs={12} md={4} className="mb-3 mb-md-0">
            <div className="stat-label">Current Budget</div>
            <div className="stat-value">{currency.format(project.budget)}</div>
          </Col>
          <Col xs={12} md={4} className="mb-3 mb-md-0">
            <div className="stat-label">Total Funded</div>
            <div className="stat-value success">
              {currency.format(project.funded)}
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="stat-label">Holdback</div>
            <div className="stat-value info">
              {currency.format(project.holdback)}
            </div>
          </Col>
        </Row>

        <div className="funded-progress">
          <div className="d-flex align-items-center justify-content-between">
            <div className="stat-label">Funded Progress</div>
            <div className="progress-text">{progressLabel}</div>
          </div>
          <ProgressBar now={progressValue} className="progress-bar" />
        </div>

        <div className="footer-stats">
          {Number.isFinite(project.ltc) && (
            <div className="footer-stat">
              <BsCurrencyDollar className="me-2" /> LTC:{" "}
              {project.ltc.toFixed(1)}%
            </div>
          )}
          {Number.isFinite(project.ltv) && (
            <div className="footer-stat">
              <BsGraphUpArrow className="me-2" /> Max LTV:{" "}
              {project.ltv.toFixed(1)}%
            </div>
          )}
        </div>

        <Button className="w-100 mt-3" onClick={() => onView(project.id)}>
          View Details
        </Button>
      </Card.Body>
    </Card>
  );
};

const ProjectDetailsPage = ({ project, onOpenModal }) => {
  const [activeTab, setActiveTab] = useState("Budget & SOV");
  const navigate = useNavigate();
  const hasLoanNumber = Boolean(project.loanNumber);
  const hasBorrower = Boolean(project.borrower);
  const hasType = Boolean(project.type);
  const completion =
    project.budget > 0 ? (project.funded / project.budget) * 100 : null;
  const completionLabel =
    completion === null ? "--" : `${completion.toFixed(1)}%`;
  const sovLines = Array.isArray(project.sovLines) ? project.sovLines : [];

  const tabs = [
    { key: "Budget & SOV", icon: <BsClipboardData />, label: "Budget & SOV" },
    { key: "Current Draw", icon: <BsFileText />, label: "Current Draw" },
    { key: "Documents", icon: <BsFileEarmarkText />, label: "Documents" },
    { key: "Milestones", icon: <BsFlag />, label: "Milestones" },
    {
      key: "Communication",
      icon: <BsChatSquareDots />,
      label: "Communication",
    },
  ];

  return (
    <div className="lender-draws-dashboard">
      <Container fluid="lg">
        <div className="details-header">
          <div className="d-flex align-items-start gap-3">
            <div className="title-icon">
              <BsFileEarmarkText size={18} />
            </div>
            <div>
              <h2 className="mb-1">{project.address}</h2>
              <div className="page-subtitle">Loan Servicing Platform</div>
            </div>
          </div>
          <div className="avatar-circle">D</div>
        </div>

        <div className="breadcrumb-line">
          <Button
            variant="link"
            className="back-link"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeft /> Back
          </Button>
          <span>Dashboard</span> <BsChevronRight /> <span>Projects</span>{" "}
          <BsChevronRight />
          <span>{project.address}</span>
        </div>

        <div className="details-meta">
          <div>
            {hasLoanNumber && <span>Loan: {project.loanNumber}</span>}
            {hasLoanNumber && hasBorrower && <span> • </span>}
            {hasBorrower && <span>Borrower: {project.borrower}</span>}
            {!hasLoanNumber && !hasBorrower && (
              <span>No loan details available</span>
            )}
          </div>
          <Button onClick={onOpenModal}>
            <BsPlus className="me-2" /> New Draw Request
          </Button>
        </div>

        <Row className="g-3 mt-2">
          {[
            { label: "Project Type", value: hasType ? project.type : "--" },
            { label: "Current Budget", value: currency.format(project.budget) },
            {
              label: "Total Funded",
              value: currency.format(project.funded),
              valueClass: "success",
            },
            {
              label: "Holdback Remaining",
              value: currency.format(project.holdback),
              valueClass: "info",
            },
            { label: "Completion", value: completionLabel },
          ].map((item) => (
            <Col key={item.label} xs={12} md={6} lg={2}>
              <Card className="summary-card">
                <Card.Body>
                  <div className="summary-label">{item.label}</div>
                  <div className={`summary-value ${item.valueClass || ""}`}>
                    {item.value}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="tab-pill-group">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? "outline-primary" : "light"}
              className={`tab-pill ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon} {tab.label}
            </Button>
          ))}
        </div>

        {activeTab === "Budget & SOV" && (
          <Card className="content-card">
            <Card.Body>
              <div className="content-title">Schedule of Values (SOV)</div>
              {sovLines.length === 0 ? (
                <div className="empty-state">No SOV lines available.</div>
              ) : (
                <Table responsive className="sov-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Original</th>
                      <th>Current</th>
                      <th>% Complete</th>
                      <th>Prior Funded</th>
                      <th>Retainage</th>
                      <th>Eligible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sovLines.map((line) => (
                      <tr key={line.id || line.category}>
                        <td>{line.category}</td>
                        <td>{line.description}</td>
                        <td>{currency.format(coerceNumber(line.original))}</td>
                        <td>{currency.format(coerceNumber(line.current))}</td>
                        <td>
                          <div className="progress-inline">
                            <ProgressBar now={coerceNumber(line.complete)} />
                            <span>
                              {coerceNumber(line.complete).toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td>
                          {currency.format(coerceNumber(line.priorFunded))}
                        </td>
                        <td>{currency.format(coerceNumber(line.retainage))}</td>
                        <td className="text-success">
                          {currency.format(coerceNumber(line.eligible))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        )}

        {activeTab === "Current Draw" && (
          <Card className="content-card">
            <Card.Body className="empty-state">
              No active draw request. Click "New Draw Request" to start one.
            </Card.Body>
          </Card>
        )}

        {activeTab === "Documents" && (
          <Row className="g-3">
            <Col lg={8}>
              <Card className="content-card">
                <Card.Body>
                  <div className="content-title">
                    Intelligent Document Processing
                  </div>
                  <div className="content-subtitle">
                    Upload PDFs, Excel files, or images with construction loan
                    and budget data
                  </div>
                  <div className="upload-dropzone large">
                    <div className="upload-icon">
                      <BsUpload size={28} />
                    </div>
                    <div className="upload-text">
                      Drag and drop your files or click to browse
                    </div>
                    <Button className="mt-2">Select File</Button>
                    <div className="upload-meta">
                      Supports PDF, Excel, CSV, and images (max 20MB)
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="content-card">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="content-title mb-0">Real-Time Updates</div>
                    <span className="updates-badge">9</span>
                  </div>
                  <div className="updates-list">
                    {activityUpdates.length === 0 ? (
                      <div className="empty-state">No updates yet.</div>
                    ) : (
                      activityUpdates.map((item) => (
                        <div className="updates-item" key={item.id}>
                          <div className="status-dot" />
                          <div>
                            <div className="updates-title">{item.title}</div>
                            <div className="updates-subtitle">
                              {item.subtitle}
                            </div>
                            <div className="updates-time">{item.time}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {activeTab === "Milestones" && (
          <Card className="content-card">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div className="content-title mb-0">Project Milestones</div>
                <Button>
                  <BsPlus className="me-2" /> Add Milestone
                </Button>
              </div>
              <div className="empty-state mt-4">
                No milestones yet. Add your first milestone to track progress.
              </div>
            </Card.Body>
          </Card>
        )}

        {activeTab === "Communication" && (
          <Row className="g-3">
            <Col lg={4}>
              <Card className="content-card">
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="content-title mb-0">Conversations</div>
                    <Button variant="outline-primary" size="sm">
                      <BsPlus className="me-2" /> New
                    </Button>
                  </div>
                  <div className="empty-state">
                    No conversations yet. Start one!
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={8}>
              <Card className="content-card min-panel">
                <Card.Body className="empty-state">
                  Select a conversation or start a new one
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

const STATUS_CONFIG = {
  SUBMITTED: { label: "Submitted", tabClass: "tab-submitted" },
  REJECTED:  { label: "Rejected",  tabClass: "tab-rejected" },
  PROCESSED: { label: "Processed", tabClass: "tab-processed" },
};
const STATUS_TABS = ["All", "SUBMITTED", "REJECTED", "PROCESSED"];

const buildDrawDetailsColumns = (onLoanClick) => [
  {
    field: "expand",
    title: "",
    Header: () => null,
    Cell: ({ row }) =>
      row.original.subRows?.length > 0 ? (
        <button
          className="btn btn-sm btn-link p-0 expand-row-btn"
          {...row.getToggleRowExpandedProps({})}
        >
          {row.isExpanded ? (
            <BsChevronDown size={14} />
          ) : (
            <BsChevronRight size={14} />
          )}
        </button>
      ) : null,
    style: { width: "36px", textAlign: "center" },
  },
  {
    title: "Property / Project",
    field: "property",
    Cell: ({ value, row }) => (row.depth > 0 ? "" : value),
  },
  {
    title: "Loan Acc",
    field: "account",
    Cell: ({ value, row }) => {
      if (row.depth > 0) return "";
      const loanId = row.original.loanrecid;
      return loanId ? (
        <div
          role="button"
          className="draw-loan-acc-link"
          onClick={() => window.open(`/loan-details/${loanId}`)}
        >
          {value}
        </div>
      ) : value;
    },
  },
  {
    title: "Line Item",
    field: "lineItem",
    Cell: ({ value, row }) =>
      row.original.lineItemMissing ? (
        <span className="draw-missing-value">{value}</span>
      ) : (
        value
      ),
  },
  { title: "Amount", field: "amount" },
  {
    title: "Draw Date",
    field: "drawDate",
    Cell: ({ value, row }) => (row.depth > 0 ? "" : value),
  },
  {
    title: "Creation Date",
    field: "creationDate",
    Cell: ({ value, row }) => (row.depth > 0 ? "" : value),
  },
  {
    title: "Status",
    field: "status",
    Cell: ({ value }) =>
      value ? (
        <span className={`draw-status-badge badge-${value.toLowerCase()}`}>
          <span className="badge-dot" />
          {STATUS_CONFIG[value]?.label || value}
        </span>
      ) : null,
  },
  ...(onLoanClick ? [{
    title: "Draw Detail",
    field: "drawDetail",
    Cell: ({ row }) =>
      row.original.loanrecid ? (
        <button
          className="btn btn-sm draw-detail-btn"
          onClick={() => onLoanClick(row.original)}
        >
          View
        </button>
      ) : null,
  }] : []),
];

const REJECT_REASON_COLUMN = { title: "Reject Reason", field: "rejectReason" };

const SubmittedDrawDetailsSection = ({ items, isLoading, onLoanClick }) => {
  const [activeStatus, setActiveStatus] = useState("All");

  const allGrouped = groupAndNormalizeDrawHistory(items);
  const baseColumns = useMemo(() => buildDrawDetailsColumns(onLoanClick), [onLoanClick]);

  const counts = STATUS_TABS.reduce((acc, s) => {
    acc[s] = s === "All"
      ? allGrouped.length
      : allGrouped.filter((g) => g.status === s).length;
    return acc;
  }, {});

  const gridData = activeStatus === "All"
    ? allGrouped
    : allGrouped.filter((g) => g.status === activeStatus);

  const gridColumns = useMemo(() => {
    if (activeStatus !== "REJECTED") return baseColumns;
    const cols = [...baseColumns];
    const statusIdx = cols.findIndex((c) => c.field === "status");
    cols.splice(statusIdx, 0, REJECT_REASON_COLUMN);
    return cols;
  }, [activeStatus, baseColumns]);

  return (
    <Card className="content-card mt-4 p-5">
      <Card.Body>
        <div className="draw-details-header">
          <div className="draws-section-title mb-0">Draw Details</div>
          <div className="draw-status-tabs">
            {STATUS_TABS.map((s) => (
              <button
                key={s}
                className={`draw-status-tab ${s !== "All" ? STATUS_CONFIG[s].tabClass : "tab-all"} ${activeStatus === s ? "active" : ""}`}
                onClick={() => setActiveStatus(s)}
              >
                {s === "All" ? "All" : STATUS_CONFIG[s].label}
                <span className="draw-tab-count">{counts[s]}</span>
              </button>
            ))}
          </div>
        </div>

        <CommonGrid
          key={activeStatus}
          gridColumns={gridColumns}
          columnData={gridData}
          minHeight="100%"
          status={isLoading ? "pending" : "fulfilled"}
          hideDashForNestedRows
          hideDashForExpandColumn
          preserveEmptyFromCustomCell
        />
      </Card.Body>
    </Card>
  );
};

const LenderDrawsDashbaord = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const selectedAccountLenderId = useSelector((state) => state.account.lenderId);
  const accountOptions = useSelector((state) => state.account.accountOptions);
  const [showModal, setShowModal] = useState(false);
  const [dashboardProjects, setDashboardProjects] = useState([]);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rawDrawHistory, setRawDrawHistory] = useState([]);
  const [isLoadingDrawHistory, setIsLoadingDrawHistory] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loanStats, setLoanStats] = useState(null);
  const [isLoadingLoanStats, setIsLoadingLoanStats] = useState(false);
  const loanID = selectedAccountLenderId;
  const allLenderRecIds =
    accountOptions?.length > 0
      ? accountOptions.map((a) => a.value).join(" ")
      : loanID;
  const effectiveUserId =
    localStorage.getItem("apex_userid") || localStorage.getItem("backToOrigin");
  const hasDashboardData =
    dashboardProjects.length > 0 || dashboardSummary !== null;

  const activeProject = useMemo(() => {
    return (
      dashboardProjects.find((project) => project.id === id) ||
      dashboardProjects[0]
    );
  }, [dashboardProjects, id]);

  const summaryMetrics = useMemo(() => {
    const summary = normalizeSummary(dashboardSummary || {}, dashboardProjects);
    const showValues = !isLoading && hasDashboardData;
    return [
      {
        label: "Active Projects",
        value: showValues ? summary.activeProjects.toString() : "--",
        icon: <BsBuilding size={18} />,
        className: "summary-icon blue",
      },
      {
        label: "Total Budget",
        value: showValues ? currency.format(summary.totalBudget) : "--",
        icon: <BsFileEarmarkText size={18} />,
        className: "summary-icon sky",
      },
      {
        label: "Total Funded",
        value: showValues ? currency.format(summary.totalFunded) : "--",
        icon: <BsCurrencyDollar size={18} />,
        className: "summary-icon green",
      },
      {
        label: "Available Funds",
        value: showValues ? currency.format(summary.remainingHoldback) : "--",
        icon: <BsGraphUpArrow size={18} />,
        className: "summary-icon orange",
      },
    ];
  }, [dashboardProjects, dashboardSummary, hasDashboardData, isLoading]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchDashboardDetails() {
      setIsLoading(true);
      setIsLoadingDrawHistory(true);
      setLoadError(null);

      try {
        const [dashboardResponse, submittedResponse] = await Promise.allSettled(
          [
            axios.post(
              urls.draws.getDetailsDrawDashboard(),
              { lenderrecid: allLenderRecIds },
              getHeaders(true),
            ),
            allLenderRecIds
              ? axios.get(
                  urls.draws.getSubmittedDrawDetails(allLenderRecIds),
                  getHeaders(),
                )
              : Promise.resolve({ data: { items: [] } }),
          ],
        );

        if (dashboardResponse.status === "fulfilled") {
          const rawData = dashboardResponse.value?.data ?? {};
          const payload = rawData?.data ?? rawData?.result ?? rawData;
          const isArrayPayload = Array.isArray(payload);
          const summaryFromArray =
            isArrayPayload && Array.isArray(payload?.[0])
              ? payload[0]?.[0]
              : null;
          const summary = isArrayPayload
            ? summaryFromArray || {}
            : payload?.summary ||
              payload?.dashboardSummary ||
              payload?.metrics ||
              payload?.overview ||
              {};
          const projectsRaw = isArrayPayload
            ? payload?.[1] || []
            : payload?.projects ||
              payload?.loans ||
              payload?.properties ||
              payload?.data ||
              payload?.items ||
              [];

          const normalizedProjects = Array.isArray(projectsRaw)
            ? projectsRaw.map((item, index) => normalizeProject(item, index))
            : [];

          setDashboardSummary(summary || null);
          setDashboardProjects(normalizedProjects);
        } else {
          const dashboardError = dashboardResponse.reason;
          if (dashboardError?.name !== "CanceledError") {
            console.error("Failed to load draws dashboard details:", dashboardError);
            setLoadError("Unable to load draw dashboard details.");
          }
          setDashboardProjects([]);
          setDashboardSummary(null);
        }

        if (submittedResponse.status === "fulfilled") {
          if (!effectiveUserId) {
            console.warn(
              "[Draws] Missing user id in localStorage. Skipping submitted draw details API call.",
            );
          }
          const submittedRaw = submittedResponse.value?.data;
          const submittedItems = Array.isArray(submittedRaw)
            ? submittedRaw
            : submittedRaw?.items ||
              submittedRaw?.data ||
              submittedRaw?.result ||
              [];
          const sortedSubmittedItems = Array.isArray(submittedItems)
            ? [...submittedItems].sort((a, b) => {
                const aTime = new Date(
                  a?.creation_date || a?.request_date || a?.requestDate || 0,
                ).getTime();
                const bTime = new Date(
                  b?.creation_date || b?.request_date || b?.requestDate || 0,
                ).getTime();
                return bTime - aTime;
              })
            : [];
          setRawDrawHistory(sortedSubmittedItems);
        } else {
          const submittedError = submittedResponse.reason;
          if (submittedError?.name !== "CanceledError") {
            console.error(
              "Failed to load submitted draw requests details:",
              submittedError,
            );
          }
          setRawDrawHistory([]);
        }
      } catch (error) {
        if (error?.name !== "CanceledError") {
          console.error("Failed to load draws dashboard details:", error);
          setLoadError("Unable to load draw dashboard details.");
        }
        setDashboardProjects([]);
        setDashboardSummary(null);
      } finally {
        setIsLoading(false);
        setIsLoadingDrawHistory(false);
      }
    }

    fetchDashboardDetails();

    return () => controller.abort();
  }, [loanID, effectiveUserId]);

  const refreshDrawHistory = async () => {
    if (!allLenderRecIds) return;
    setIsLoadingDrawHistory(true);
    try {
      const res = await axios.get(
        urls.draws.getSubmittedDrawDetails(allLenderRecIds),
        getHeaders(),
      );
      const raw = res?.data;
      const items = Array.isArray(raw)
        ? raw
        : raw?.items || raw?.data || raw?.result || [];
      const sorted = Array.isArray(items)
        ? [...items].sort((a, b) => {
            const aTime = new Date(a?.creation_date || a?.request_date || a?.requestDate || 0).getTime();
            const bTime = new Date(b?.creation_date || b?.request_date || b?.requestDate || 0).getTime();
            return bTime - aTime;
          })
        : [];
      setRawDrawHistory(sorted);
    } catch (err) {
      console.error("Failed to refresh draw history:", err);
    } finally {
      setIsLoadingDrawHistory(false);
    }
  };

  // Fetch stats for a single loan when selected
  useEffect(() => {
    if (!selectedLoan?.loanrecid) {
      setLoanStats(null);
      return;
    }
    let cancelled = false;
    async function fetchLoanStats() {
      setIsLoadingLoanStats(true);
      try {
        const res = await axios.post(
          urls.draws.getDetailsDrawDashboardLoans(),
          { loanID: selectedLoan.loanrecid },
          getHeaders(true),
        );
        if (cancelled) return;
        const rawData = res?.data ?? {};
        const payload = rawData?.data ?? rawData?.result ?? rawData;
        const stats = Array.isArray(payload) && Array.isArray(payload[0])
          ? payload[0][0] || {}
          : payload || {};
        setLoanStats({
          totalBudget: coerceNumber(stats["Total Budget"] ?? stats?.totalBudget ?? stats?.total_budget ?? 0),
          totalFunded: coerceNumber(stats["Total Funded"] ?? stats?.totalFunded ?? stats?.total_funded ?? 0),
          remainingHoldback: coerceNumber(stats["Remaining Holdback"] ?? stats?.remainingHoldback ?? stats?.remaining_holdback ?? 0),
        });
      } catch (err) {
        if (!cancelled) console.error("Failed to load loan stats:", err);
      } finally {
        if (!cancelled) setIsLoadingLoanStats(false);
      }
    }
    fetchLoanStats();
    return () => { cancelled = true; };
  }, [selectedLoan?.loanrecid]);

  const handleLoanClick = (row) => {
    setSelectedLoan(row);
  };

  const handleBackToDashboard = () => {
    setSelectedLoan(null);
    setLoanStats(null);
  };

  // Filter draw history for the selected loan
  const loanDrawHistory = useMemo(() => {
    if (!selectedLoan?.loanrecid) return [];
    return rawDrawHistory.filter(
      (item) =>
        (item?.loanrecid || item?.loanRecId) === selectedLoan.loanrecid,
    );
  }, [rawDrawHistory, selectedLoan?.loanrecid]);

  const loanSummaryMetrics = useMemo(() => {
    const ready = !isLoadingLoanStats && loanStats;
    return [
      {
        label: "Total Budget",
        value: ready ? currency.format(loanStats.totalBudget) : "--",
        icon: <BsFileEarmarkText size={18} />,
        className: "summary-icon sky",
      },
      {
        label: "Total Funded",
        value: ready ? currency.format(loanStats.totalFunded) : "--",
        icon: <BsCurrencyDollar size={18} />,
        className: "summary-icon green",
      },
      {
        label: "Available Funds",
        value: ready ? currency.format(loanStats.remainingHoldback) : "--",
        icon: <BsGraphUpArrow size={18} />,
        className: "summary-icon orange",
      },
    ];
  }, [loanStats, isLoadingLoanStats]);

  if (id) {
    if (!activeProject) {
      return (
        <div className="lender-draws-dashboard">
          <Container fluid="lg">
            <div className="empty-state">No project found for this ID.</div>
          </Container>
        </div>
      );
    }
    return (
      <>
        <ProjectDetailsPage
          project={activeProject}
          onOpenModal={() => setShowModal(true)}
        />
        <NewDrawRequestModal
          show={showModal}
          onHide={() => setShowModal(false)}
          loanID={loanID}
          onSuccess={refreshDrawHistory}
          pendingDraws={rawDrawHistory}
        />
      </>
    );
  }

  if (selectedLoan) {
    return (
      <div className="lender-draws-dashboard">
        <Container fluid="lg">
          <div className="page-header">
            <div className="d-flex align-items-center gap-3">
              <button
                className="btn btn-sm btn-outline-secondary loan-back-btn"
                onClick={handleBackToDashboard}
              >
                <BsArrowLeft size={16} /> Back
              </button>
              <div>
                <h2 className="mb-0">{selectedLoan.property}</h2>
                <div className="page-subtitle">Loan # {selectedLoan.account}</div>
              </div>
            </div>
          </div>

          <Row className="g-3 mt-4">
            {loanSummaryMetrics.map((metric) => (
              <Col key={metric.label} xs={12} md={6} lg={4}>
                <SummaryCard {...metric} />
              </Col>
            ))}
          </Row>

          <SubmittedDrawDetailsSection
            items={loanDrawHistory}
            isLoading={isLoadingDrawHistory}
          />
        </Container>

        <NewDrawRequestModal
          show={showModal}
          onHide={() => setShowModal(false)}
          loanID={loanID}
          onSuccess={refreshDrawHistory}
          pendingDraws={rawDrawHistory}
        />
      </div>
    );
  }

  return (
    <div className="lender-draws-dashboard">
      <Container fluid="lg">
        <div className="page-header">
          <div className="d-flex align-items-center gap-3">
            <div className="title-icon">
              <BsFileEarmarkText size={20} />
            </div>
            <div>
              <h2 className="mb-0">Construction Draws</h2>
              <div className="page-subtitle">Loan Servicing Platform</div>
            </div>
          </div>
          <Button onClick={() => setShowModal(true)}>+ New Draw Request</Button>
        </div>

        <Row className="g-3 mt-4">
          {summaryMetrics.map((metric) => (
            <Col key={metric.label} xs={12} md={6} lg={3}>
              <SummaryCard {...metric} />
            </Col>
          ))}
        </Row>

        {loadError && <div className="mt-3 text-danger small">{loadError}</div>}

        {/* <div className="search-wrapper">
          <Form.Control
            type="text"
            placeholder="Search projects by address, borrower, or loan ID..."
            className="search-input"
          />
          <BsSearch className="search-icon" />
        </div> */}

        {/* <div className="section-title">Active Projects</div> */}

        {/* {dashboardProjects.length === 0 ? (
          <div className="empty-state mt-4">
            {isLoading ? "Loading projects..." : "No projects found."}
          </div>
        ) : (
          <Row className="g-4">
            {dashboardProjects.map((project) => (
              <Col key={project.id} xs={12} lg={6}>
                <ProjectCard
                  project={project}
                  onView={(pid) => navigate(`/lender-draws-dashboard/${pid}`)}
                />
              </Col>
            ))}
          </Row>
        )} */}

        <SubmittedDrawDetailsSection
          items={rawDrawHistory}
          isLoading={isLoadingDrawHistory}
          onLoanClick={handleLoanClick}
        />
      </Container>

      <NewDrawRequestModal
        show={showModal}
        onHide={() => setShowModal(false)}
        loanID={loanID}
        onSuccess={refreshDrawHistory}
        pendingDraws={rawDrawHistory}
      />
    </div>
  );
};

export default LenderDrawsDashbaord;
